// Реестр поверхностей: папка — это и есть регистрация.
//
// Почему так. Поверхности строят разные таски одной волны. Пока реестр —
// список в общем файле, каждая новая поверхность правит этот общий файл, и два
// таска одной волны сталкиваются на нём же (`interfaces.md` §2.6 :169).
// Здесь реестр собирается обходом папок при сборке: появилась
// `src/surfaces/S-NN/` с тремя файлами — поверхность в системе; папку убрали —
// её нет, и ни одной общей строки при этом не тронуто.
//
// ⚠ **Разбор отделён от обхода намеренно.** `import.meta.glob` — это сборка:
// его нельзя ни позвать в тесте, ни подставить ему свою карту. Поэтому весь
// разбор живёт в `buildRegistry(metas, views)`, которому карту модулей подают
// снаружи, а обход папок — четыре строки в самом низу файла. Иначе механизм
// проверялся бы только целой сборкой, то есть не проверялся бы.

import type { ComponentType } from "react";

// ⚠ Форма кода ввозится, а не записывается здесь второй раз: та же форма нужна
// генератору тек, и две записи разошлись бы молча (`./code.mjs`).
export { SURFACE_CODE } from "./code.mjs";
import { SURFACE_CODE } from "./code.mjs";

/** Имя теки в ключе карты модулей: `./S-52/meta.ts` → `S-52`. */
const FOLDER = /^\.\/([^/]+)\/(?:meta\.ts|index\.tsx)$/;

/** Что поверхность объявляет о себе. Лежит в `S-NN/meta.ts`. */
export interface SurfaceMeta {
  /** Код поверхности, `S-NN`. Совпадает с именем теки. */
  code: string;
  /** Назва поверхні українською — та, что видит человек. */
  title_uk: string;
  /** Адрес в браузере. Без него поверхность некуда открыть. */
  path: string;
}

/** Строка реестра: объявление плюс отложенная загрузка экрана. */
export interface Surface extends SurfaceMeta {
  /** Экран грузится при первом открытии, а не при старте клиента. */
  load: () => Promise<{ default: ComponentType }>;
}

/** Дефект теки поверхности. Называет теку и то, чего в ней не хватает. */
export class SurfaceDefect extends Error {}

function metaOf(key: string, module: unknown): SurfaceMeta {
  const folder = FOLDER.exec(key)?.[1];
  if (folder === undefined) {
    throw new SurfaceDefect(`${key}: не схоже на файл теки поверхні`);
  }
  const meta = (module as { meta?: unknown } | null)?.meta;
  if (meta === undefined || meta === null || typeof meta !== "object") {
    throw new SurfaceDefect(`${key}: у файлі немає export const meta`);
  }
  const { code, title_uk, path } = meta as Partial<SurfaceMeta>;
  // ⚠ Код проверяется формой, а не «есть строка»: `S-1`, `s-52`, `SCR-9.2` и
  // пустая строка — всё это не коды поверхностей, и молча попавшая в реестр
  // поверхность с таким кодом развалила бы адреса и права.
  if (typeof code !== "string" || !SURFACE_CODE.test(code)) {
    throw new SurfaceDefect(
      `${key}: код поверхні «${String(code)}» не у форматі S-NN`,
    );
  }
  if (code !== folder) {
    throw new SurfaceDefect(
      `${key}: тека «${folder}», а код у meta — «${code}»`,
    );
  }
  if (typeof title_uk !== "string" || title_uk.trim() === "") {
    throw new SurfaceDefect(`${key}: немає назви title_uk`);
  }
  if (typeof path !== "string" || !path.startsWith("/")) {
    throw new SurfaceDefect(`${key}: адреса «${String(path)}» має починатися з «/»`);
  }
  // ⚠ Название читается в момент показа, а не замораживается при сборке реестра:
  // оно — слово словаря (`G117`), а словарь приходит позже, чем собран реестр.
  // Копия значения здесь печатала код («s01_title») в названии блока дашборда и
  // пункта «Відкрити сторінку» (таск 27).
  const declared = meta as SurfaceMeta;
  return {
    code,
    path,
    get title_uk() {
      return declared.title_uk;
    },
  };
}

/**
 * Карты модулей → реестр поверхностей.
 *
 * `metas` — `{"./S-52/meta.ts": модуль}`, `views` — `{"./S-52/index.tsx": загрузчик}`.
 * Порядок — по коду поверхности: реестр обязан быть одинаковым от прогона к
 * прогону, а порядок обхода папок таким не бывает.
 */
export function buildRegistry(
  metas: Record<string, unknown>,
  views: Record<string, unknown>,
): Surface[] {
  const byFolder = new Map<string, () => Promise<{ default: ComponentType }>>();
  for (const [key, loader] of Object.entries(views)) {
    const folder = FOLDER.exec(key)?.[1];
    if (folder === undefined) continue;
    byFolder.set(folder, loader as () => Promise<{ default: ComponentType }>);
  }

  const found: Surface[] = [];
  const seen = new Map<string, string>();
  for (const [key, module] of Object.entries(metas)) {
    const meta = metaOf(key, module);
    const load = byFolder.get(meta.code);
    if (load === undefined) {
      // Тека с объявлением, но без экрана: поверхность видна в меню и не
      // открывается. Такое молчать нельзя — это и есть пустой экран.
      throw new SurfaceDefect(`${meta.code}: є meta.ts, але немає index.tsx`);
    }
    const twice = seen.get(meta.code);
    if (twice !== undefined) {
      throw new SurfaceDefect(`${meta.code}: оголошено двічі — ${twice} і ${key}`);
    }
    seen.set(meta.code, key);
    found.push({
      code: meta.code,
      path: meta.path,
      get title_uk() {
        return meta.title_uk;
      },
      load,
    });
  }
  return found.sort((a, b) => a.code.localeCompare(b.code));
}

// --- Обход папок при сборке --------------------------------------------------
//
// ⚠ Единственное место, где живёт `import.meta.glob`. Объявления читаются сразу
// (`eager`) — из них строятся адреса и меню; экраны грузятся при открытии.
const META_FILES = import.meta.glob("./*/meta.ts", { eager: true });
const VIEW_FILES = import.meta.glob("./*/index.tsx");

/** Все поверхности, которые есть в сборке. */
export const surfaces: Surface[] = buildRegistry(META_FILES, VIEW_FILES);
